import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Tiles,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import { IdentifierType, Shift, BulkCreateSignupSingleShiftResponse } from '@amzn/red-velvet-api';
import { useBulkCreateSignup } from '../../hooks/signup/useBulkCreateSignup';
import { AmazonSignup } from '../../utils/signupUtils';
import { DismissConfirmationAlert } from './signupTableCommon';
import { SignupSearchTable } from './signupSearchTable';
import { BulkAddSignupsContent } from './bulkAddSignupsContent';
import { ValidateSignupsContent } from './validateSignupsContent';

export interface BulkSignupModalForShiftProps {
  visible: boolean;
  onDismiss: () => void;
  eventId: string;
  shift: Shift;
  existingSignups: AmazonSignup[];
  remainingSpots: number;
  metricKeys: {
    searchPeopleFilterMetricKey: string;
    addSignupFromSearchMetricKey: string;
    validateSignupsMetricKey: string;
    validationSuccessCountMetricKey: string;
    validationErrorsCountMetricKey: string;
    validationSkippedCountMetricKey: string;
    addSignupFromBulkMetricKey: string;
  };
}

type ModalTileOption = 'search' | 'bulk';
type ModalStep = 'add' | 'validate';

export function BulkSignupModalForShift({
  visible,
  onDismiss,
  eventId,
  shift,
  existingSignups,
  remainingSpots,
  metricKeys,
}: BulkSignupModalForShiftProps) {
  const { t } = useTranslation('signup');
  const bulkCreateSignup = useBulkCreateSignup(eventId);
  const shiftId = shift.shiftId;

  const [selectedTile, setSelectedTile] = useState<ModalTileOption>('search');
  const [pendingSignupAliases, setPendingSignupAliases] = useState<Set<string>>(new Set());

  // Bulk add paste flow state (owned here so "Previous" preserves input)
  const [currentStep, setCurrentStep] = useState<ModalStep>('add');
  const [validationData, setValidationData] = useState<BulkCreateSignupSingleShiftResponse | null>(
    null
  );
  const [bulkIdentifierType, setBulkIdentifierType] = useState<IdentifierType>(
    IdentifierType.ALIAS
  );
  const [bulkSignupsList, setBulkSignupsList] = useState('');
  const [showDismissAlert, setShowDismissAlert] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const isLoading = bulkCreateSignup.isPending || isBulkSubmitting;
  const effectiveRemainingSpots = remainingSpots - pendingSignupAliases.size;

  const resetAllState = useCallback(() => {
    setPendingSignupAliases(new Set());
    setSelectedTile('search');
    setCurrentStep('add');
    setValidationData(null);
    setBulkIdentifierType(IdentifierType.ALIAS);
    setBulkSignupsList('');
    setShowDismissAlert(false);
    setIsBulkSubmitting(false);
  }, []);

  const handleAddToSignupList = useCallback((alias: string) => {
    setPendingSignupAliases((prev) => new Set(prev).add(alias));
  }, []);

  const handleRemoveFromSignupList = useCallback((alias: string) => {
    setPendingSignupAliases((prev) => {
      const next = new Set(prev);
      next.delete(alias);
      return next;
    });
  }, []);

  const handleBulkAddToSignupList = useCallback((aliases: string[]) => {
    setPendingSignupAliases((prev) => {
      const next = new Set(prev);
      aliases.forEach((alias) => next.add(alias));
      return next;
    });
  }, []);

  const handleDone = async () => {
    if (pendingSignupAliases.size === 0) return;

    const signupUsers = Array.from(pendingSignupAliases).map((alias) => ({
      identifierType: IdentifierType.ALIAS,
      identifierValue: alias,
    }));

    await bulkCreateSignup.mutateAsync({
      validationOnly: false,
      shiftSignupUsersMap: { [shiftId]: signupUsers },
    });
    window.location.reload();
  };

  const handleCancel = () => {
    if (isLoading) return;
    setShowDismissAlert(true);
  };

  const handleConfirmDismiss = () => {
    resetAllState();
    onDismiss();
  };

  const handleCancelDismiss = () => {
    setShowDismissAlert(false);
  };

  const handleTileChange = (value: ModalTileOption) => {
    setSelectedTile(value);
    if (value === 'search') {
      // Reset bulk state when switching to search
      setCurrentStep('add');
      setValidationData(null);
      setBulkIdentifierType(IdentifierType.ALIAS);
      setPendingSignupAliases(new Set());
    }
  };

  const handleValidationSuccess = (
    data: BulkCreateSignupSingleShiftResponse,
    identifierType: IdentifierType
  ) => {
    setValidationData(data);
    setBulkIdentifierType(identifierType);
    setCurrentStep('validate');
  };

  const handlePrevious = () => {
    setCurrentStep('add');
    // Do NOT reset bulkSignupsList or bulkIdentifierType — preserves user input
  };

  const handleComplete = () => {
    window.location.reload();
  };

  const tileOptions = [
    {
      label: t('bulkSignupModal.tiles.search.label'),
      description: t('bulkSignupModal.tiles.search.description'),
      value: t('bulkSignupModal.tiles.search.value'),
    },
    {
      label: t('bulkSignupModal.tiles.bulk.label'),
      description: t('bulkSignupModal.tiles.bulk.description'),
      value: t('bulkSignupModal.tiles.bulk.value'),
    },
  ];

  const renderSearchContent = () => (
    <div className="bulk-upload-modal-content">
      <Tiles
        columns={2}
        items={tileOptions}
        value={selectedTile}
        onChange={({ detail }) => handleTileChange(detail.value as ModalTileOption)}
      />
      <div className="bulk-upload-modal-scrollable">
        <SignupSearchTable
          key={visible ? 'open' : 'closed'}
          existingSignups={existingSignups}
          pendingSignupAliases={pendingSignupAliases}
          onAddToSignupList={handleAddToSignupList}
          onRemoveFromSignupList={handleRemoveFromSignupList}
          onBulkAddToSignupList={handleBulkAddToSignupList}
          isAtCapacity={effectiveRemainingSpots <= 0}
          effectiveRemainingSpots={effectiveRemainingSpots}
          metricKeys={metricKeys}
        />
      </div>
      {pendingSignupAliases.size > 0 && (
        <Box padding={{ top: 's' }}>
          <StatusIndicator type="pending">
            {t('bulkSignupModal.pendingSummary', {
              count: pendingSignupAliases.size,
              aliases: Array.from(pendingSignupAliases).join(', '),
            })}
          </StatusIndicator>
        </Box>
      )}
      <Box padding={{ top: 'l' }}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleCancel} disabled={bulkCreateSignup.isPending}>
              {t('signupSearchTable.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleDone}
              loading={bulkCreateSignup.isPending}
              disabled={pendingSignupAliases.size === 0}
            >
              {t('signupSearchTable.done')}
            </Button>
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );

  const renderBulkAddContent = () => (
    <div className="bulk-upload-modal-content">
      <Tiles
        columns={2}
        items={tileOptions}
        value={selectedTile}
        onChange={({ detail }) => handleTileChange(detail.value as ModalTileOption)}
      />
      <BulkAddSignupsContent
        eventId={eventId}
        shiftId={shiftId}
        bulkSignupsList={bulkSignupsList}
        onBulkSignupsListChange={setBulkSignupsList}
        identifierTypeValue={bulkIdentifierType}
        onIdentifierTypeChange={setBulkIdentifierType}
        onValidationSuccess={handleValidationSuccess}
        onCancel={handleCancel}
        metricKeys={metricKeys}
      />
    </div>
  );

  const renderValidateContent = () => {
    if (!validationData) return null;
    return (
      <div className="bulk-upload-modal-content">
        <ValidateSignupsContent
          validationData={validationData}
          identifierType={bulkIdentifierType}
          shift={shift}
          eventId={eventId}
          remainingSpots={remainingSpots}
          onCancel={handleCancel}
          onPrevious={handlePrevious}
          onComplete={handleComplete}
          onSubmittingChange={setIsBulkSubmitting}
          metricKeys={metricKeys}
        />
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      size="max"
      header={t('bulkSignupModal.header')}
    >
      <div className="bulk-upload-modal-container">
        <DismissConfirmationAlert
          visible={showDismissAlert}
          onStay={handleCancelDismiss}
          onLeave={handleConfirmDismiss}
        />
        {selectedTile === 'search' && renderSearchContent()}
        {selectedTile === 'bulk' && currentStep === 'add' && renderBulkAddContent()}
        {selectedTile === 'bulk' && currentStep === 'validate' && renderValidateContent()}
      </div>
    </Modal>
  );
}
